import { QueryParams, Service } from '@/services/api';
import { Contributor } from '@/types/tower/v2/Contributor';
import { ProgramTower } from '@/types/tower/v2/Tower';
import {
  TowerCardData,
  TowerTypeData
} from '@/views/tower_dashboard/types/tower-dashboard';

export interface GetTowersParams {
  towerIds?: string[];
  userId?: number;
  programId?: number;
  accountId?: number;
  versionLabel?: string;
  towerTypeId?: number;
  towerName?: string;
}

export interface GetTowerCardsParams {
  userId?: number;
  searchQuery: string;
  towerTypeId?: number;
  contributor?: boolean;
  accountId?: number;
  programId?: number;
  showCurrentOnly?: boolean;
}

export interface UpdateTowerInfoRequest {
  versionLabel?: string;
  accountId?: number;
  towerTypeId?: number;
  programId?: number;
  published?: boolean;
}

// Save tower action to differentiate between an update and a new tower save.
export type SaveTowerAction = '' | 'new';

export interface SaveTowerRequest {
  tower: ProgramTower;
  action?: SaveTowerAction;
}

class TowerService extends Service {
  async getTowers(queryParams?: GetTowersParams): Promise<ProgramTower[]> {
    return this.get<ProgramTower[]>('/v2/towers', queryParams as unknown as QueryParams).then(this.handleResponse);
  }

  async getTowersByAccount(id: number): Promise<ProgramTower[]> {
    return this.get<ProgramTower[]>(`/v2/account/${id}/towers`).then(this.handleResponse);
  }

  async getTowersByAccountAndProgram(accountId: number, programId: number): Promise<ProgramTower[]> {
    return this.get<ProgramTower[]>(`/v2/account/${accountId}/programs/${programId}/towers`).then(this.handleResponse);
  }

  async getTowerByTowerId(towerId: string): Promise<ProgramTower> {
    return this.get<ProgramTower>(`/v2/towers/${towerId}`).then(this.handleResponse);
  }

  async getTowerCards(queryParams?: GetTowerCardsParams): Promise<TowerCardData[]> {
    return this.get<TowerCardData[]>(`/v2/towers/cards`, queryParams as unknown as QueryParams).then(this.handleResponse);
  }

  async getPublishedTowerCountByAccount(accountId: number | string, programId?: number): Promise<number> {
    return await this.get<number>(`/v2/account/${accountId}/towers/published/count`, { programId }).then(this.handleResponse);
  }

  async getTowerContributors(towerId: string): Promise<Contributor[]> {
    return this.get<Contributor[]>(`/v2/towers/contributors`, { towerId: towerId }).then(this.handleResponse);
  }

  async getTowerTypes(): Promise<TowerTypeData[]> {
    return this.get<TowerTypeData[]>(`/v2/towers/types`).then(this.handleResponse);
  }

  async saveTower(request: SaveTowerRequest): Promise<ProgramTower> {
    return this.post<ProgramTower>('/v2/towers', request).then(this.handleResponse);
  }

  async createTower(request: Partial<ProgramTower>): Promise<ProgramTower> {
    return this.post<ProgramTower>('/v2/towers/create', request).then(this.handleResponse);
  }

  async updateTowerInfo(towerId: string, request: UpdateTowerInfoRequest): Promise<boolean> {
    return this.patch<boolean>(`/v2/towers/${towerId}`, request).then(this.handleResponse);
  }

  async updateTower(tower: ProgramTower): Promise<ProgramTower> {
    return this.patch<ProgramTower>('/v2/towers', tower).then(this.handleResponse);
  }

  async deleteTower(towerId: string): Promise<boolean> {
    return this.delete<boolean>(`/v2/towers/${towerId}`).then(this.handleResponse);
  }
}

export const towerService = new TowerService();
